import React, { useEffect, useState } from 'react';
import { Parent } from '@/types/models';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { UserPlus, Mail, PencilLine, Eye, EyeOff } from 'lucide-react';
import InviteStatusBadge from '../InviteStatusBadge';
import { useAuth } from '@/hooks/useAuth';
import { useFamilyUtils } from '../family-list/useFamilyUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface ParentsSectionProps {
  parents: Parent[];
  onAddParent: () => void;
  onSendInvite: (parentId: string) => void;
  onEditParent: (parent: Parent) => void;
  onDeleteParent?: (parent: Parent) => void;
  onToggleActiveStatus?: (parent: Parent, isActive: boolean) => void;
  expanded?: boolean;
}

const ParentsSection = ({
  parents,
  onAddParent,
  onSendInvite,
  onEditParent,
  onDeleteParent,
  onToggleActiveStatus,
  expanded = false
}: ParentsSectionProps) => {
  const {
    user,
    userRole
  } = useAuth();
  const {
    isBizOwnerOrAdmin
  } = useFamilyUtils();
  const hasAdminPermission = isBizOwnerOrAdmin();

  // Filter out soft-deleted parents
  const activeParents = parents.filter(parent => !parent.is_deleted);

  // Add debugging logs
  useEffect(() => {
    console.log('ParentsSection - Auth Debug:', {
      userRole,
      userData: user,
      hasAdminPermission,
      isBizAdmin: userRole === 'bizadmin',
      expanded
    });
  }, [user, userRole, hasAdminPermission, expanded]);

  const handleStatusToggle = (parent: Parent, isActive: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleActiveStatus) {
      onToggleActiveStatus(parent, isActive);
    }
  };

  if (!activeParents || activeParents.length === 0) {
    return (
      <div className="p-2">
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground mb-4">No parents in this family</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activeParents.map(parent => (
        <Card 
          key={parent.id} 
          className={`relative hover:border-primary/50 transition-all cursor-pointer ${expanded ? 'p-4' : 'p-2'} ${!parent.is_active ? 'bg-gray-50/50 border-gray-300 hover:border-gray-400' : ''}`}
        >
          <CardContent className="p-0" onClick={() => onEditParent(parent)}>
            <div className="flex justify-between items-start">
              <div className={!parent.is_active ? 'opacity-60' : ''}>
                <div className="font-medium flex items-center gap-2">
                  {parent.first_name} {parent.last_name}
                  {parent.is_primary && (
                    <span className="ml-1 text-xs bg-blue-100 text-blue-800 py-0.5 px-1.5 rounded-full">
                      Primary
                    </span>
                  )}
                  {!parent.is_active && (
                    <span className="ml-1 text-xs bg-gray-500 text-white py-0.5 px-1.5 rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {parent.email || 'No email'}
                </div>
                {expanded && parent.phone && (
                  <div className="text-sm text-muted-foreground mt-1">
                    {parent.phone}
                  </div>
                )}
                {expanded && parent.address_city && parent.address_state && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {[parent.address_city, parent.address_state, parent.address_country !== 'United States' ? parent.address_country : ''].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
              <div className="flex items-start gap-2">
                <InviteStatusBadge status={parent.invite_status} />
              </div>
            </div>
            
            {/* Action buttons - shown for all users (simplified UI) */}
            {expanded && (
              <div className="mt-4 flex gap-2">
                {/* Edit button shown for all users */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1" 
                        onClick={e => {
                          e.stopPropagation();
                          onEditParent(parent);
                        }}
                      >
                        <PencilLine className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit parent details</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                {/* Send Invite button (only shown for pending) */}
                {parent.invite_status === 'pending' && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1" 
                          onClick={e => {
                            console.log('=== SEND INVITE BUTTON CLICKED ===');
                            console.log('Parent ID:', parent.id);
                            console.log('Parent email:', parent.email);
                            console.log('onSendInvite function:', typeof onSendInvite);
                            e.stopPropagation();
                            try {
                              onSendInvite(parent.id);
                              console.log('onSendInvite called successfully');
                            } catch (error) {
                              console.error('Error calling onSendInvite:', error);
                            }
                          }}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Send Invite
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Send invitation email</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                
                {/* Admin-only buttons */}
                {hasAdminPermission && onToggleActiveStatus && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1" 
                          onClick={e => handleStatusToggle(parent, !parent.is_active, e)}
                        >
                          {parent.is_active ? (
                            <>
                              <EyeOff className="h-4 w-4 mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{parent.is_active ? 'Deactivate account' : 'Activate account'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ParentsSection;
